import { useEffect, useState } from 'react';
import { fetchDaytradeBackdrop, fetchMarketBrief3, fetchMarketForecast, fetchSectorLeadership } from '../api';
import { GLOSSARY } from '../interpret';
import { splitHotCold } from '../sectorSplit';
import { DaytradeBackdrop, MarketForecast, MarketOutlookSignals, SectorResponse } from '../types';

// 🌦️ 초보자 데크 — 시장탭 재설계(07-07): "쉬움 ≠ 얕음" 원칙.
// 모든 카드는 ①실측 숫자 병기 ②'왜?' 펼침(근거 원문) ③기존 전문가 콘텐츠는 아래 '자세히'에 완전 보존.
// 재료는 전부 기존 데이터의 번역 — 신규 생성은 3줄 요약(AI·근거 헤드라인 동봉)뿐.
//
// 🎨 규격 V2(08-23) — 카드 상자를 헤어라인 구획으로 해체, 거대 이모지 히어로를 활자 히어로로,
//   신호등·온도계를 저채도 토큰으로. 판정 로직·문구·데이터는 그대로다(표현 계층만 교체).

type Brief3 = { lines: string[]; sources: string[]; made_at?: string; _fallback?: boolean };
type Sig = MarketOutlookSignals | null;

const KICKER = 'text-[10.5px] font-[650] tracking-[.12em] text-dc-ink3';
const PLAIN = 'tracking-normal font-normal';   // 키커 안에 붙는 설명 문구(자간 원복)
// 🌡️ 온도·감정 그라데이션 표준형 — 왼쪽=차가움(겁먹음) → 오른쪽=뜨거움(들뜸). 바늘은 앰버.
const TEMP_GRAD = 'linear-gradient(90deg, rgba(92,143,214,.55), #232B3A 45%, #232B3A 55%, rgba(224,108,91,.55))';

const WEATHER_BIG: Record<string, { icon: string; title: string }> = {
  '패닉 급락일': { icon: '⛈️', title: '오늘은 폭풍우' },
  '변동성 폭발일': { icon: '🌪️', title: '오늘은 회오리바람' },
  '추세 상승일': { icon: '☀️', title: '오늘은 맑음' },
  '소수 쏠림장': { icon: '⚠️', title: '오늘은 부분 맑음' },
  '반등 시도일': { icon: '🌤️', title: '오늘은 갬' },
  '약세 흐름일': { icon: '🌧️', title: '오늘은 비' },
  '눈치 횡보장': { icon: '☁️', title: '오늘은 흐림' },
};

function pickWord(): { term: string; desc: string } | null {
  if (!GLOSSARY.length) return null;
  const day = Math.floor(Date.now() / 86400000);
  return GLOSSARY[day % GLOSSARY.length];
}

export default function EasyMarketDeck({ sig }: { sig: Sig }) {
  const [bd, setBd] = useState<DaytradeBackdrop | null>(null);
  const [fc, setFc] = useState<MarketForecast | null>(null);
  const [b3, setB3] = useState<Brief3 | null>(null);
  const [sectors, setSectors] = useState<SectorResponse | null>(null);
  const [whyOpen, setWhyOpen] = useState<string>('');
  useEffect(() => {
    fetchDaytradeBackdrop().then(setBd).catch(() => {});
    fetchMarketForecast().then(setFc).catch(() => {});
    fetchSectorLeadership().then(setSectors).catch(() => {});
    fetchMarketBrief3().then(setB3).catch(() => {});
  }, []);

  const ch = bd?.character || null;
  const wx = ch ? (WEATHER_BIG[ch.character] || { icon: ch.emoji, title: ch.character }) : null;
  const ai = fc?.today?.ai;
  const fcStats = fc?.stats;
  const kp = bd?.kospi_pct ?? null;

  // 🚦 밤새 보유 판단 — 진입 점수 0~100의 3색 번역(원 숫자·체크리스트는 '왜?'에)
  const score = sig?.entry?.score ?? null;
  const light = score == null ? null : score >= 70 ? '좋음' : score >= 40 ? '주의' : '멈춤';
  const lightWhy = sig?.entry?.checks || [];

  // 🌡️ 온도계 — 공포탐욕 번역
  const fg = sig?.fear_greed?.kr || null;

  // 🔥 뜨는것/식는것 — 섹터 RS 상·하위 (겹침 방지는 splitHotCold가 담당 · 회귀 테스트 sectorSplit.test.ts)
  const secs = (sectors?.sectors || []).filter((s) => s.count >= 2);
  const { hot, cold } = splitHotCold(secs);

  const word = pickWord();
  const events = bd?.events || [];

  const Why = ({ id, children }: { id: string; children: React.ReactNode }) => (
    whyOpen === id
      ? <div className="mt-1 text-[11.5px] leading-[1.6] text-dc-ink2 border-t border-dc-hair pt-2 text-left">{children}</div>
      : null
  );
  const WhyBtn = ({ id }: { id: string }) => (
    <button onClick={() => setWhyOpen((v) => (v === id ? '' : id))} className={`text-[10.5px] text-dc-point/85 hover:text-dc-point ml-1.5 ${PLAIN}`}>{whyOpen === id ? '▾ 접기' : '▸ 왜?'}</button>
  );

  return (
    <div className="space-y-5">
      {/* ── 🌦️ 히어로 — 오늘 장 성격을 활자로(이모지는 문구 속 소형) ── */}
      <div className="flex flex-col gap-2 pb-5 border-b border-dc-hair">
        <div className="flex items-baseline gap-3 flex-wrap">
          <span className="text-[27px] font-[760] tracking-[-.015em] text-dc-ink1">
            {wx?.title ?? '데이터 모으는 중'} <span className="text-[20px]">{wx?.icon ?? '🌫️'}</span>
          </span>
          {kp != null && (
            <span className="text-[14px] text-dc-ink2">
              코스피 <span className={`font-bold ${kp >= 0 ? 'text-dc-up' : 'text-dc-down'}`}>{kp >= 0 ? '+' : ''}{kp.toFixed(1)}%</span>
            </span>
          )}
        </div>
        {ch && <div className="text-[15.5px] leading-[1.55] text-dc-ink2">{ch.copy}</div>}
        <div className="flex items-center gap-2 flex-wrap text-[11.5px] text-dc-ink3">
          {/* 🔮 '오늘 예보 · 장 성격' 칩은 2026-08-22 제거 — 적중 21.9%(n32)로 낙제. 히어로에 크게 뜨는
              장 성격(wx·ch)은 **KIS 실시간 관측값**이라 그대로다(예보가 아니라 지금 일어나는 일). */}
          {fcStats?.ai && fcStats.ai.n > 0 && <span title="이 예보가 지난 며칠 실제로 얼마나 맞았는지 — 매일 종가로 자동 채점돼요">예보 적중 {fcStats.ai.win}% (지난 {fcStats.ai.n}일)</span>}
          <WhyBtn id="hero" />
        </div>
        <Why id="hero">
          <p className="mb-1 text-dc-ink3">이 장 성격은 KIS 실시간 시장폭·지수 궤적·프로그램 매매로 계산한 값이에요(사람 감이 아님).</p>
          {ch && ch.reasons.length > 0 && <p>판정 근거: {ch.reasons.join(' · ')}</p>}
          {ai?.reasons && ai.reasons.length > 0 && <p className="mt-1">예보 근거: {ai.reasons.join(' · ')}</p>}
          {bd?.saga?.line && <p className="mt-1">맥락: {bd.saga.line}</p>}
        </Why>
      </div>

      {/* ── 오늘 무슨 일이? 3줄 요약 ── */}
      {b3 && (
        <div className="flex flex-col gap-2 pb-5 border-b border-dc-hair">
          <p className={KICKER}>오늘 무슨 일이? — 3줄 요약{b3._fallback ? ' (계산값 요약)' : ''}<WhyBtn id="b3" /></p>
          <div className="text-[15.5px] leading-[1.55] text-dc-ink1 space-y-1">
            {b3.lines.map((l, i) => <p key={i}><span className="text-dc-point font-bold">{['①', '②', '③'][i]}</span> {l}</p>)}
          </div>
          <Why id="b3">
            <p className="text-dc-ink3 mb-1">이 요약이 근거로 삼은 실제 뉴스 제목들:</p>
            {b3.sources.map((s, i) => <p key={i}>· {s}</p>)}
          </Why>
        </div>
      )}

      {/* ── 🚦 밤새 보유 판단 + 🌡️ 온도계 ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-5 pb-5 border-b border-dc-hair">
        <div className="flex flex-col gap-3">
          <p className={KICKER}>지금 사도 되나요?<WhyBtn id="light" /></p>
          <div className="flex items-center gap-3">
            {(['멈춤', '주의', '좋음'] as const).map((l) => {
              const on = light === l;
              // 켜진 구는 그 뜻의 색 — 상태 3단(전 화면 공통): 멈춤=warn / 주의=앰버 / 좋음=ok. 꺼진 구는 잉크 테두리만.
              //   up(상승색)은 방향 전용이라 '멈춤'에 쓰지 않는다 — 오르는 중이라는 뜻으로 읽힌다.
              const cls = l === '멈춤' ? 'border-dc-warn/60 bg-dc-warn/10 text-dc-warn' : l === '주의' ? 'border-dc-point/60 bg-dc-point/10 text-dc-point' : 'border-dc-ok/60 bg-dc-ok/10 text-dc-ok';
              return (
                <span
                  key={l}
                  className={`rounded-full border-[1.5px] inline-flex items-center justify-center ${on ? `w-[46px] h-[46px] text-[11.5px] font-bold ${cls}` : 'w-10 h-10 text-[10.5px] border-dc-line1 text-[#4A5567]'}`}
                >{l}</span>
              );
            })}
          </div>
          <p className="text-[13px] leading-[1.6] text-dc-ink2">{light === '멈춤' ? '폭풍이 지날 때까지 구경만 — 살 자리가 오면 알림이 알려드려요' : light === '주의' ? '골라서 조금만 — 확실한 자리만' : light === '좋음' ? '조건이 잘 갖춰진 날이에요 — 그래도 분산·손절은 기본' : '점수 계산 중'}
            {score != null && <span className="text-dc-ink3"> · 진입점수 {score}/100</span>}</p>
          <Why id="light">
            {lightWhy.length ? lightWhy.slice(0, 6).map((c, i) => <p key={i}><span className={c.met ? 'text-dc-ok' : 'text-dc-down'}>{c.met ? '✓' : '✗'}</span> {c.name} <span className="text-dc-ink3">({c.current} / 목표 {c.want})</span>{c.core ? ' ★' : ''}</p>) : <p>체크리스트 로딩 중 — 아래 '자세히 보기'의 진입 신호판이 원본이에요.</p>}
          </Why>
        </div>
        <div className="flex flex-col gap-3">
          <p className={KICKER}>시장 기분 온도계<WhyBtn id="temp" /></p>
          {fg ? (
            <>
              <div className="flex flex-col gap-2 pt-1">
                <div className="relative h-[5px] rounded-full max-w-[420px]" style={{ background: TEMP_GRAD }}>
                  <span className="absolute top-[-4px] w-[2px] h-[13px] rounded-[2px] bg-dc-point" style={{ left: `${Math.min(97, Math.max(1, fg.score))}%` }} />
                </div>
                <div className="flex justify-between max-w-[420px] text-[11.5px] text-dc-ink3"><span>잔뜩 겁먹음</span><span>들떠 있음</span></div>
              </div>
              <p className="text-[13px] leading-[1.6] text-dc-ink2">지금 <span className="text-dc-ink1 font-bold">{fg.score}점</span> — {fg.label}
                {fg.score <= 25 && <span className="text-dc-ink3"> (역사적으로 이 근처가 바닥이었던 적이 많아요)</span>}
                {fg.score >= 75 && <span className="text-dc-ink3"> (과열 신호 — 욕심을 조심할 때)</span>}
              </p>
            </>
          ) : <p className="text-[11.5px] text-dc-ink3">지수 불러오는 중</p>}
          <Why id="temp">
            <p>변동성·시장폭·수급·모멘텀을 합성한 계산값이에요. 미국(CNN) 온도계와 요소 분해는 아래 '자세히 보기'의 공포·탐욕 지수가 원본입니다.</p>
          </Why>
        </div>
      </div>

      {/* ── 🔥 뜨는것/식는것 + 📅 이번 주 ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-5 pb-5 border-b border-dc-hair">
        <div className="flex flex-col gap-2">
          <p className={KICKER}>요즘 돈이 몰리는 곳 / 빠지는 곳 <span className={PLAIN}>(상대강도 기준)</span></p>
          <div className="text-[13px] leading-[1.6] space-y-1">
            {hot.map((s) => <p key={s.sector}><span className="text-dc-up font-bold">▲ {s.sector}</span> <span className="text-dc-ink3">RS {Math.round(s.avg_rs)} ·</span> <span className="text-dc-ink2">{s.top?.[0]?.name}</span></p>)}
            {cold.map((s) => <p key={s.sector}><span className="text-dc-down font-bold">▼ {s.sector}</span> <span className="text-dc-ink3">RS {Math.round(s.avg_rs)}</span></p>)}
            {!!secs.length && !cold.length && <p className="text-dc-ink3">빠지는 곳은 표시할 만큼 데이터가 없어요</p>}
            {!secs.length && <p className="text-dc-ink3">스캔 데이터 로딩 중</p>}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <p className={KICKER}>조심할 날 (달력에서 확정)</p>
          <div className="text-[13px] leading-[1.7] text-dc-ink2">
            {events.length ? events.map((e) => <p key={e.name}><span className="text-dc-ink1 font-bold">오늘</span> — {e.name} <span className="text-dc-ink3">{e.note}</span></p>) : <p className="text-dc-ink3">오늘 예정 이벤트 없음</p>}
            <p className="text-dc-ink3 mt-1">전체 일정은 아래 '자세히 보기' 이벤트 레이더에</p>
          </div>
        </div>
      </div>

      {/* ── 🎓 오늘의 한 단어 ── */}
      {word && (
        <div className="flex items-start gap-3 text-[13px]">
          <span className={`${KICKER} shrink-0 pt-1`}>오늘의 한 단어</span>
          <p className="text-dc-ink2 leading-[1.6]"><b className="text-dc-ink1 font-[750]">{word.term}</b> — {word.desc}</p>
        </div>
      )}
    </div>
  );
}
